import { useEffect, useState } from "react";
import api from "../lib/api";
import { Clock, Send, CheckCircle2, XCircle, Plus, Hourglass } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

/**
 * Staff-facing "Missed Punch Request" widget.
 *
 * Lives at the top of the Employee Portal / StylistDay page. Staff can submit
 * a request for a forgotten clock-in or clock-out; pending + recent reviewed
 * items are listed below the submit button.
 */
export default function MissedPunchCard() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ date: "", start: "", end: "", location_id: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/timeclock-requests/my?limit=10");
      setList(res.data || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    api.get("/locations").then(r => setLocations(r.data || [])).catch(() => {});
  }, []);

  const resetForm = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ date: today, start: "", end: "", location_id: "", reason: "" });
  };

  const openDialog = () => { resetForm(); setOpen(true); };

  const submit = async () => {
    if (!form.date || !form.start) {
      toast.error("Date and start time are required");
      return;
    }
    const startIso = new Date(`${form.date}T${form.start}`).toISOString();
    const endIso = form.end ? new Date(`${form.date}T${form.end}`).toISOString() : null;
    if (endIso && new Date(endIso) <= new Date(startIso)) {
      toast.error("End time must be after start time");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/timeclock-requests", {
        requested_clock_in: startIso,
        requested_clock_out: endIso,
        reason: form.reason.trim(),
        location_id: form.location_id || null,
      });
      toast.success("Request sent to your manager");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
    setSubmitting(false);
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  const pending = list.filter(r => r.status === "pending");
  const recent = list.filter(r => r.status !== "pending").slice(0, 3);

  return (
    <>
      <div data-testid="missed-punch-card" className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            <h3 className="font-['Outfit'] text-base font-medium text-slate-900">Missed a punch?</h3>
          </div>
          <Button
            size="sm"
            onClick={openDialog}
            data-testid="open-missed-punch-btn"
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-8 px-4 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Submit request
          </Button>
        </div>
        <p className="text-xs text-slate-400 font-['Manrope'] mb-4">
          Forgot to clock in or out? Send the details and a manager can approve — approved requests show up on your TimeClock as a normal shift.
        </p>

        {loading ? (
          <div className="h-5 w-32 bg-slate-100 rounded animate-pulse" />
        ) : list.length === 0 ? (
          <p className="text-xs text-slate-400 font-['Manrope']">No requests yet.</p>
        ) : (
          <div className="space-y-2">
            {pending.length > 0 && (
              <div data-testid="pending-requests-section">
                <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold mb-1.5 font-['Manrope']">Awaiting review</p>
                {pending.map(r => (
                  <div key={r.request_id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`my-req-${r.request_id}`}>
                    <Hourglass className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span className="text-slate-700 flex-1">
                      {fmt(r.requested_clock_in)}{r.requested_clock_out && ` → ${fmt(r.requested_clock_out)}`}
                    </span>
                    <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded">Pending</span>
                  </div>
                ))}
              </div>
            )}
            {recent.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5 mt-2 font-['Manrope']">Recent</p>
                {recent.map(r => {
                  const isApproved = r.status === "approved";
                  const Icon = isApproved ? CheckCircle2 : XCircle;
                  const iconCls = isApproved ? "text-emerald-500" : "text-rose-500";
                  return (
                    <div key={r.request_id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`my-req-${r.request_id}`}>
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${iconCls}`} />
                      <span className="text-slate-700 flex-1">
                        {fmt(r.requested_clock_in)}{r.requested_clock_out && ` → ${fmt(r.requested_clock_out)}`}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${isApproved ? "text-emerald-600 bg-emerald-50 border border-emerald-100" : "text-rose-600 bg-rose-50 border border-rose-100"}`}>
                        {r.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px] bg-white rounded-2xl p-8">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">Missed Punch Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">Date *</label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                max={new Date().toISOString().slice(0, 10)}
                className="h-10 border-slate-200 rounded-lg"
                data-testid="mp-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">Clock-in time *</label>
                <Input
                  type="time"
                  value={form.start}
                  onChange={e => setForm(f => ({ ...f, start: e.target.value }))}
                  className="h-10 border-slate-200 rounded-lg"
                  data-testid="mp-start" />
              </div>
              <div>
                <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">Clock-out (optional)</label>
                <Input
                  type="time"
                  value={form.end}
                  onChange={e => setForm(f => ({ ...f, end: e.target.value }))}
                  className="h-10 border-slate-200 rounded-lg"
                  data-testid="mp-end" />
              </div>
            </div>
            {locations.length > 1 && (
              <div>
                <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">Location</label>
                <select
                  value={form.location_id}
                  onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm"
                  data-testid="mp-location">
                  <option value="">— Select —</option>
                  {locations.map(l => (
                    <option key={l.location_id} value={l.location_id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">Reason</label>
              <textarea
                rows={3}
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value.slice(0, 500) }))}
                placeholder="e.g. forgot to clock in when I started my 2pm color"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none outline-none focus:border-rose-300"
                data-testid="mp-reason" />
              <p className="text-[10px] text-slate-400 mt-0.5 text-right">{form.reason.length}/500</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="flex-1 border-slate-200 rounded-full h-11"
                data-testid="mp-cancel">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11"
                data-testid="mp-submit">
                <Send className="w-4 h-4 mr-1.5" />
                {submitting ? "Sending…" : "Send Request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
